import { useCallback, useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "../components/ui/alert-dialog";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "../components/ui/dialog";
import { Trash2, Users, Plus, GraduationCap, QrCode, Award, FileDown, Copy, Sparkles, IdCard } from "lucide-react";
import { toast } from "sonner";
import DailyAttendanceTab from "../components/training/DailyAttendanceTab";
import HandoutTab from "../components/training/HandoutTab";

const emptyForm = {
  title: "", client_name: "", audience: "", audience_size: 0,
  modality: "presencial", hours: 0, location: "", starts_at: "",
  scope: "", methodology: "", prerequisites: "", instructor_name: "",
  pass_grade: 70, pass_attendance: 70,
};

const BACKEND = process.env.REACT_APP_BACKEND_URL;

export default function TrainingEditor() {
  const { id } = useParams();
  const navigate = useNavigate();
  const isNew = !id || id === "new";
  const [form, setForm] = useState(emptyForm);
  const [data, setData] = useState(null);
  const [participants, setParticipants] = useState([]);
  const [newP, setNewP] = useState({ name: "", email: "", document: "", role: "" });
  const [sessions, setSessions] = useState([]);
  const [newSess, setNewSess] = useState({ label: "", hours: 1 });
  const [certs, setCerts] = useState([]);
  const [qrModal, setQrModal] = useState(null); // {session_id, url}

  const load = async () => {
    if (isNew || !id) return;
    const r = await api.get(`/trainings/${id}`);
    setForm({ ...emptyForm, ...r.data });
    setData(r.data);
    setParticipants(r.data.participants || []);
    const s = await api.get(`/trainings/${id}/attendance-sessions`);
    setSessions(s.data);
    const c = await api.get(`/trainings/${id}/certificates`);
    setCerts(c.data);
  };
  // load uses id/isNew via closure; keeping it stable keeps the effect self-contained
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [id, isNew]);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    const payload = { ...form, audience_size: +form.audience_size, hours: +form.hours, pass_grade: +form.pass_grade, pass_attendance: +form.pass_attendance };
    if (isNew) {
      const r = await api.post("/trainings", payload);
      toast.success("Treinamento criado");
      navigate(`/trainings/${r.data.training_id}`);
    } else {
      const r = await api.put(`/trainings/${id}`, payload);
      setData(r.data);
      toast.success("Salvo");
    }
  };

  const remove = async () => {
    await api.delete(`/trainings/${id}`);
    toast.success("Treinamento excluído");
    navigate("/trainings");
  };

  const addParticipant = async () => {
    if (!newP.name) return toast.error("Nome é obrigatório");
    const r = await api.post(`/trainings/${id}/participants`, [newP]);
    setParticipants(r.data);
    setNewP({ name: "", email: "", document: "", role: "" });
    toast.success("Participante adicionado");
  };

  const removeParticipant = async (pid) => {
    await api.delete(`/trainings/${id}/participants/${pid}`);
    setParticipants((ps) => ps.filter((p) => p.participant_id !== pid));
  };

  const createSession = async () => {
    const r = await api.post(`/trainings/${id}/attendance-sessions`, newSess);
    setSessions((s) => [r.data, ...s]);
    setNewSess({ label: "", hours: 1 });
    setQrModal({ session_id: r.data.session_id, url: `${window.location.origin}/presenca/${r.data.token}` });
    toast.success("Sessão criada — compartilhe o QR");
  };

  const closeSession = async (sid) => {
    await api.post(`/attendance-sessions/${sid}/close`);
    toast.success("Sessão encerrada. Frequência atualizada.");
    load();
  };

  const saveScore = async (pid, patch) => {
    await api.patch(`/trainings/${id}/participants/${pid}/score`, patch);
    setParticipants((ps) => ps.map((p) => p.participant_id === pid ? { ...p, ...patch } : p));
  };

  const issueCerts = async () => {
    const r = await api.post(`/trainings/${id}/issue-certificates`);
    if (r.data.issued.length) toast.success(`${r.data.issued.length} certificado(s) emitido(s)`);
    if (r.data.skipped.length) toast.info(`${r.data.skipped.length} pulado(s): ${r.data.skipped.map(s => s.name).join(", ")}`);
    load();
  };

  return (
    <div className="p-10 max-w-[1400px]">
      <div className="flex items-end justify-between mb-8">
        <div>
          <p className="overline">{data?.training_id?.slice(0, 12) || "Novo treinamento"}</p>
          <h1 className="font-heading text-4xl mt-2 flex items-center gap-3">
            <GraduationCap size={28} strokeWidth={1.3} className="text-[#C07C88]" />
            {isNew ? "Novo Treinamento" : form.title || "Editar Treinamento"}
          </h1>
        </div>
        <div className="flex gap-2">
          {!isNew && data && (
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button variant="outline" className="border-red-200 text-red-600 hover:bg-red-50" data-testid="delete-training-btn">
                  <Trash2 size={14} className="mr-1" /> Excluir
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>Excluir "{data.title}"?</AlertDialogTitle>
                  <AlertDialogDescription>Esta ação é permanente. Todos os participantes, sessões de presença e certificados serão removidos.</AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>Cancelar</AlertDialogCancel>
                  <AlertDialogAction className="bg-red-600 hover:bg-red-700 text-white" onClick={remove} data-testid="confirm-delete-training">Excluir</AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          )}
          <Button className="bg-[#C07C88] hover:bg-[#A86571]" onClick={save} data-testid="save-training-btn">Salvar</Button>
        </div>
      </div>

      <Tabs defaultValue="info" className="w-full">
        <TabsList className="mb-6 bg-white border border-border">
          <TabsTrigger value="info" data-testid="tab-info">Informações</TabsTrigger>
          <TabsTrigger value="participants" disabled={isNew} data-testid="tab-participants">Participantes</TabsTrigger>
          <TabsTrigger value="daily" disabled={isNew} data-testid="tab-daily">Presença Diária</TabsTrigger>
          <TabsTrigger value="attendance" disabled={isNew} data-testid="tab-attendance">Presença (QR)</TabsTrigger>
          <TabsTrigger value="scores" disabled={isNew} data-testid="tab-scores">Notas & NPS</TabsTrigger>
          <TabsTrigger value="certificates" disabled={isNew} data-testid="tab-certificates">Certificados</TabsTrigger>
          <TabsTrigger value="documents" disabled={isNew} data-testid="tab-documents">Documentos</TabsTrigger>
          <TabsTrigger value="handout" disabled={isNew} data-testid="tab-handout">Apostila</TabsTrigger>
          <TabsTrigger value="portal" disabled={isNew} data-testid="tab-portal">Portal do Aluno</TabsTrigger>
        </TabsList>

        <TabsContent value="info">
          <div className="grid lg:grid-cols-3 gap-6">
            <div className="lg:col-span-2 space-y-6">
              <Card className="p-6 shadow-none border-border">
                <p className="overline mb-4">Informações Gerais</p>
                <div className="grid md:grid-cols-2 gap-4">
                  <div className="md:col-span-2"><Label>Título</Label><Input value={form.title} onChange={(e) => update("title", e.target.value)} data-testid="training-title" /></div>
                  <div><Label>Cliente</Label><Input value={form.client_name} onChange={(e) => update("client_name", e.target.value)} data-testid="training-client" /></div>
                  <div><Label>Instrutor</Label><Input value={form.instructor_name} onChange={(e) => update("instructor_name", e.target.value)} data-testid="training-instructor" /></div>
                  <div><Label>Público</Label><Input value={form.audience} onChange={(e) => update("audience", e.target.value)} /></div>
                  <div><Label>Vagas</Label><Input type="number" value={form.audience_size} onChange={(e) => update("audience_size", e.target.value)} /></div>
                  <div>
                    <Label>Modalidade</Label>
                    <Select value={form.modality} onValueChange={(v) => update("modality", v)}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="presencial">Presencial</SelectItem>
                        <SelectItem value="online">Online</SelectItem>
                        <SelectItem value="híbrido">Híbrido</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div><Label>Carga horária</Label><Input type="number" step="0.5" value={form.hours} onChange={(e) => update("hours", e.target.value)} /></div>
                  <div><Label>Local</Label><Input value={form.location} onChange={(e) => update("location", e.target.value)} /></div>
                  <div><Label>Início</Label><Input type="date" value={form.starts_at?.slice(0,10) || ""} onChange={(e) => update("starts_at", e.target.value)} /></div>
                </div>
              </Card>
              <Card className="p-6 shadow-none border-border">
                <p className="overline mb-4">Escopo & Metodologia</p>
                <div className="space-y-4">
                  <div><Label>Escopo</Label><Textarea rows={6} value={form.scope} onChange={(e) => update("scope", e.target.value)} /></div>
                  <div><Label>Metodologia</Label><Textarea rows={3} value={form.methodology} onChange={(e) => update("methodology", e.target.value)} /></div>
                  <div><Label>Pré-requisitos</Label><Textarea rows={2} value={form.prerequisites} onChange={(e) => update("prerequisites", e.target.value)} /></div>
                </div>
              </Card>
            </div>
            <Card className="p-6 shadow-none border-border h-fit">
              <p className="overline mb-4">Regras de Certificação</p>
              <div className="space-y-4">
                <div><Label>Nota mínima (%)</Label><Input type="number" value={form.pass_grade} onChange={(e) => update("pass_grade", e.target.value)} data-testid="pass-grade" /></div>
                <div><Label>Frequência mínima (%)</Label><Input type="number" value={form.pass_attendance} onChange={(e) => update("pass_attendance", e.target.value)} data-testid="pass-attendance" /></div>
                <div className="border-t border-border pt-4 text-xs text-muted-foreground">
                  Certificado emitido para <span className="text-[#D4AF37] font-semibold">nota ≥ {form.pass_grade}%</span> E <span className="text-[#D4AF37] font-semibold">presença ≥ {form.pass_attendance}%</span> E NPS respondido.
                </div>
              </div>
            </Card>
          </div>
        </TabsContent>

        <TabsContent value="participants">
          <Card className="p-6 shadow-none border-border">
            <div className="flex items-center justify-between mb-4">
              <p className="overline flex items-center gap-2"><Users size={12} /> Participantes ({participants.length})</p>
              <Button size="sm" variant="outline" asChild disabled={!participants.length} data-testid="badges-pdf-btn">
                <a href={`${BACKEND}/api/trainings/${id}/badges/pdf`} target="_blank" rel="noreferrer"><IdCard size={13} className="mr-1" /> Crachás PDF (4 por folha)</a>
              </Button>
            </div>
            <div className="grid grid-cols-12 gap-2 mb-4">
              <Input className="col-span-4" placeholder="Nome" value={newP.name} onChange={(e) => setNewP({ ...newP, name: e.target.value })} data-testid="new-p-name" />
              <Input className="col-span-3" placeholder="E-mail" value={newP.email} onChange={(e) => setNewP({ ...newP, email: e.target.value })} data-testid="new-p-email" />
              <Input className="col-span-2" placeholder="CPF" value={newP.document} onChange={(e) => setNewP({ ...newP, document: e.target.value })} />
              <Input className="col-span-2" placeholder="Cargo" value={newP.role} onChange={(e) => setNewP({ ...newP, role: e.target.value })} />
              <Button className="col-span-1 bg-[#C07C88] hover:bg-[#A86571]" onClick={addParticipant} data-testid="add-participant-btn"><Plus size={14} /></Button>
            </div>
            <div className="divide-y divide-border">
              {participants.map((p) => (
                <div key={p.participant_id} className="py-2 flex items-center gap-3" data-testid={`participant-row-${p.participant_id}`}>
                  {p.has_photo
                    ? <img src={`${BACKEND}/api/trainings/${id}/participants/${p.participant_id}/photo`} alt="" className="w-9 h-9 rounded-full object-cover" data-testid={`participant-photo-${p.participant_id}`} />
                    : <span className="w-9 h-9 rounded-full bg-[#F7EDEF] text-[#C07C88] text-xs font-semibold flex items-center justify-center">{p.name.slice(0, 1).toUpperCase()}</span>}
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-sm truncate">{p.name} {p.self_enrolled && <span className="ml-1 text-[10px] uppercase tracking-wider text-[#D4AF37]">auto-inscrito</span>}</p>
                    <p className="text-xs text-muted-foreground truncate">{[p.email, p.document, p.company || p.role].filter(Boolean).join(" · ")}</p>
                  </div>
                  <span className="text-xs text-muted-foreground">Presença: {p.attendance_pct || 0}%</span>
                  <button onClick={() => removeParticipant(p.participant_id)} className="p-1.5 text-muted-foreground hover:text-red-500"><Trash2 size={13} /></button>
                </div>
              ))}
              {participants.length === 0 && <p className="text-xs text-muted-foreground text-center py-6">Sem participantes ainda</p>}
            </div>
          </Card>
        </TabsContent>

        <TabsContent value="daily">
          {!isNew && <DailyAttendanceTab trainingId={id} />}
        </TabsContent>

        <TabsContent value="handout">
          {!isNew && <HandoutTab trainingId={id} />}
        </TabsContent>

        <TabsContent value="attendance">
          <Card className="p-6 shadow-none border-border mb-4">
            <p className="overline mb-4 flex items-center gap-2"><QrCode size={12} /> Nova sessão de presença</p>
            <div className="grid grid-cols-12 gap-2">
              <Input className="col-span-7" placeholder="Rótulo (ex: Aula 1 — Introdução)" value={newSess.label} onChange={(e) => setNewSess({ ...newSess, label: e.target.value })} data-testid="new-session-label" />
              <Input className="col-span-3" type="number" step="0.5" placeholder="Horas" value={newSess.hours} onChange={(e) => setNewSess({ ...newSess, hours: e.target.value })} data-testid="new-session-hours" />
              <Button className="col-span-2 bg-[#C07C88] hover:bg-[#A86571]" onClick={createSession} data-testid="create-session-btn">Gerar QR</Button>
            </div>
          </Card>
          <div className="space-y-3">
            {sessions.map((s) => {
              const url = `${window.location.origin}/presenca/${s.token}`;
              return (
                <Card key={s.session_id} className="p-5 shadow-none border-border" data-testid={`session-${s.session_id}`}>
                  <div className="flex items-start justify-between">
                    <div>
                      <p className="font-medium">{s.label} <span className="text-xs text-muted-foreground">· {s.hours}h · {s.check_ins?.length || 0} presentes</span></p>
                      <p className="text-xs text-muted-foreground mt-1">{s.created_at?.slice(0, 19).replace("T", " ")}</p>
                    </div>
                    <div className="flex gap-2 items-center">
                      {s.closed ? (
                        <span className="text-xs px-2 py-1 rounded-full bg-muted text-muted-foreground">ENCERRADA</span>
                      ) : (
                        <>
                          <Button size="sm" variant="outline" onClick={() => setQrModal({ session_id: s.session_id, url })} data-testid={`show-qr-${s.session_id}`}><QrCode size={13} className="mr-1" /> QR</Button>
                          <Button size="sm" variant="outline" onClick={() => { navigator.clipboard.writeText(url); toast.success("Link copiado"); }}><Copy size={13} className="mr-1" /> Link</Button>
                          <Button size="sm" variant="outline" onClick={() => closeSession(s.session_id)} data-testid={`close-session-${s.session_id}`}>Encerrar</Button>
                        </>
                      )}
                    </div>
                  </div>
                  {s.check_ins?.length > 0 && (
                    <div className="mt-3 pt-3 border-t border-border text-xs">
                      <p className="overline mb-2">Lista consolidada</p>
                      <div className="grid md:grid-cols-2 gap-1">
                        {s.check_ins.map((ci) => (
                          <div key={ci.participant_id} className="flex items-center justify-between py-1">
                            <span>{ci.name}</span>
                            <span className="text-muted-foreground">{ci.signed_at?.slice(11,16)}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </Card>
              );
            })}
            {sessions.length === 0 && <p className="text-xs text-muted-foreground text-center py-6">Nenhuma sessão criada ainda. Crie uma para gerar o QR de presença.</p>}
          </div>

          <Dialog open={!!qrModal} onOpenChange={(v) => !v && setQrModal(null)}>
            <DialogContent className="max-w-md">
              <DialogHeader><DialogTitle>QR Code de Presença</DialogTitle></DialogHeader>
              {qrModal && (
                <div className="text-center space-y-4">
                  <img
                    src={`${BACKEND}/api/attendance-sessions/${qrModal.session_id}/qr`}
                    alt="QR"
                    className="mx-auto w-64 h-64 border border-border rounded-md p-2 bg-white"
                    data-testid="qr-image"
                  />
                  <p className="text-xs text-muted-foreground break-all">{qrModal.url}</p>
                  <Button variant="outline" onClick={() => { navigator.clipboard.writeText(qrModal.url); toast.success("Link copiado"); }} className="w-full"><Copy size={14} className="mr-1" /> Copiar link público</Button>
                </div>
              )}
            </DialogContent>
          </Dialog>
        </TabsContent>

        <TabsContent value="scores">
          <Card className="p-6 shadow-none border-border">
            <p className="overline mb-4">Notas & Pesquisa NPS</p>
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs uppercase tracking-wider text-muted-foreground">
                  <th className="py-2">Participante</th>
                  <th className="py-2 w-24">Presença</th>
                  <th className="py-2 w-28">Nota (%)</th>
                  <th className="py-2 w-24">NPS (0-10)</th>
                  <th className="py-2">Comentário</th>
                </tr>
              </thead>
              <tbody>
                {participants.map((p) => (
                  <tr key={p.participant_id} className="border-t border-border" data-testid={`score-row-${p.participant_id}`}>
                    <td className="py-2">
                      <p className="font-medium">{p.name}</p>
                      <p className="text-xs text-muted-foreground">{p.email}</p>
                    </td>
                    <td className="py-2 text-xs">{p.attendance_pct || 0}%</td>
                    <td className="py-2"><Input type="number" min="0" max="100" defaultValue={p.grade ?? ""} onBlur={(e) => saveScore(p.participant_id, { grade: e.target.value === "" ? null : +e.target.value })} data-testid={`grade-${p.participant_id}`} /></td>
                    <td className="py-2"><Input type="number" min="0" max="10" defaultValue={p.nps_score ?? ""} onBlur={(e) => saveScore(p.participant_id, { nps_score: e.target.value === "" ? null : +e.target.value })} data-testid={`nps-${p.participant_id}`} /></td>
                    <td className="py-2"><Input defaultValue={p.nps_comment || ""} onBlur={(e) => saveScore(p.participant_id, { nps_comment: e.target.value })} placeholder="Feedback" /></td>
                  </tr>
                ))}
              </tbody>
            </table>
            {participants.length === 0 && <p className="text-xs text-muted-foreground text-center py-6">Adicione participantes primeiro</p>}
          </Card>
        </TabsContent>

        <TabsContent value="certificates">
          <Card className="p-6 shadow-none border-border mb-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="overline flex items-center gap-2"><Award size={12} /> Emissão automática</p>
                <p className="text-xs text-muted-foreground mt-1">Emite para participantes com nota ≥ {form.pass_grade}%, presença ≥ {form.pass_attendance}% e NPS respondido.</p>
              </div>
              <Button className="bg-[#D4AF37] hover:bg-[#B8961F] text-white" onClick={issueCerts} data-testid="issue-certs-btn">
                <Sparkles size={14} className="mr-1" /> Emitir certificados
              </Button>
            </div>
          </Card>
          <div className="grid md:grid-cols-2 gap-3">
            {certs.map((c) => (
              <Card key={c.certificate_id} className="p-5 shadow-none border-border" data-testid={`cert-${c.certificate_id}`}>
                <div className="flex items-start justify-between">
                  <div>
                    <p className="font-heading text-lg">{c.participant_name}</p>
                    <p className="text-xs text-muted-foreground">{c.participant_email}</p>
                    <div className="flex gap-3 text-xs mt-2">
                      <span>Nota: <span className="text-[#D4AF37] font-semibold">{c.grade}%</span></span>
                      <span>Frequência: <span className="text-[#D4AF37] font-semibold">{c.attendance_pct}%</span></span>
                    </div>
                    <p className="text-[10px] font-mono text-muted-foreground mt-2">{c.verification_code}</p>
                  </div>
                  <div className="flex flex-col gap-2">
                    <Button size="sm" variant="outline" asChild data-testid={`cert-pdf-${c.certificate_id}`}>
                      <a href={`${BACKEND}/api/certificates/${c.certificate_id}/pdf`} target="_blank" rel="noreferrer"><FileDown size={13} className="mr-1" /> PDF</a>
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => { navigator.clipboard.writeText(`${window.location.origin}/verificar/${c.verification_code}`); toast.success("Link copiado"); }}>
                      <Copy size={13} className="mr-1" /> Link
                    </Button>
                  </div>
                </div>
              </Card>
            ))}
            {certs.length === 0 && <p className="text-xs text-muted-foreground text-center py-6 md:col-span-2">Nenhum certificado emitido ainda</p>}
          </div>
        </TabsContent>

        <TabsContent value="documents">
          <div className="grid md:grid-cols-2 gap-4">
            <Card className="p-6 shadow-none border-border">
              <p className="overline mb-3">📊 Relatório Técnico Pós-Treinamento</p>
              <p className="text-sm text-muted-foreground mb-4">
                PDF executivo com dados gerais, indicadores de desempenho (nota média, frequência, NPS, taxa de aprovação), escopo executado, sessões, detalhamento por participante, certificados emitidos e comentários NPS.
              </p>
              <Button asChild className="w-full bg-[#C07C88] hover:bg-[#A86571]" data-testid="tech-report-btn">
                <a href={`${BACKEND}/api/trainings/${id}/technical-report/pdf`} target="_blank" rel="noreferrer">
                  <FileDown size={14} className="mr-2" /> Baixar Relatório PDF
                </a>
              </Button>
            </Card>
            <CollabTermCard trainingId={id} training={form} />
          </div>
        </TabsContent>

        <TabsContent value="portal">
          <PortalTab trainingId={id} training={form || data} onReload={load} />
        </TabsContent>
      </Tabs>
    </div>
  );
}

function PortalTab({ trainingId, training, onReload }) {
  const [cfg, setCfg] = useState({
    enrollment_open: training?.enrollment_open || false,
    materials_public: training?.materials_public || false,
    consent_enabled: training?.consent_enabled !== false,
    consent_text: training?.consent_text || "",
  });
  const [material, setMaterial] = useState({ name: "", kind: "link", url_or_data: "", description: "" });
  const [materials, setMaterials] = useState(training?.materials || []);
  const [slug, setSlug] = useState(training?.public_slug || "");
  const [consents, setConsents] = useState(null);

  useEffect(() => {
    setMaterials(training?.materials || []);
    setSlug(training?.public_slug || "");
    setCfg({
      enrollment_open: training?.enrollment_open || false,
      materials_public: training?.materials_public || false,
      consent_enabled: training?.consent_enabled !== false,
      consent_text: training?.consent_text || "",
    });
  }, [training]);

  const saveCfg = async () => {
    const r = await api.post(`/trainings/${trainingId}/portal-config`, cfg);
    setSlug(r.data.public_slug);
    toast.success("Portal atualizado");
    if (onReload) onReload();
  };

  const addMaterial = async () => {
    if (!material.name || !material.url_or_data) return toast.error("Nome e URL/arquivo obrigatórios");
    const r = await api.post(`/trainings/${trainingId}/materials`, { ...material, size: material.url_or_data.length });
    setMaterials(r.data.materials);
    setMaterial({ name: "", kind: "link", url_or_data: "", description: "" });
    toast.success("Material adicionado");
  };

  const onFilePick = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 8 * 1024 * 1024) return toast.error("Arquivo grande demais (máx 8MB)");
    const reader = new FileReader();
    reader.onload = () => setMaterial((m) => ({ ...m, kind: "file", url_or_data: reader.result, name: m.name || f.name }));
    reader.readAsDataURL(f);
  };

  const removeMaterial = async (mid) => {
    await api.delete(`/trainings/${trainingId}/materials/${mid}`);
    setMaterials((ms) => ms.filter((m) => m.material_id !== mid));
  };

  const loadConsents = useCallback(async () => {
    const r = await api.get(`/trainings/${trainingId}/consents`);
    setConsents(r.data);
  }, [trainingId]);
  useEffect(() => { loadConsents(); }, [loadConsents]);

  const portalUrl = slug ? `${window.location.origin}/portal/${slug}` : "";

  return (
    <div className="space-y-4">
      <Card className="p-6 shadow-none border-border">
        <div className="flex items-center justify-between mb-4">
          <p className="overline">Configuração do Portal Público</p>
          <Button size="sm" className="bg-[#C07C88] hover:bg-[#A86571]" onClick={saveCfg} data-testid="save-portal-cfg">Salvar</Button>
        </div>
        <div className="grid md:grid-cols-3 gap-4">
          <label className="flex items-start gap-3 p-3 border border-border rounded-md cursor-pointer">
            <input type="checkbox" checked={cfg.enrollment_open} onChange={(e) => setCfg({ ...cfg, enrollment_open: e.target.checked })} data-testid="cfg-enrollment" />
            <div>
              <p className="font-medium text-sm">Inscrições abertas</p>
              <p className="text-xs text-muted-foreground">Clientes se auto-matriculam via link</p>
            </div>
          </label>
          <label className="flex items-start gap-3 p-3 border border-border rounded-md cursor-pointer">
            <input type="checkbox" checked={cfg.materials_public} onChange={(e) => setCfg({ ...cfg, materials_public: e.target.checked })} data-testid="cfg-materials" />
            <div>
              <p className="font-medium text-sm">Biblioteca visível</p>
              <p className="text-xs text-muted-foreground">Alunos acessam PDFs/links</p>
            </div>
          </label>
          <label className="flex items-start gap-3 p-3 border border-border rounded-md cursor-pointer">
            <input type="checkbox" checked={cfg.consent_enabled} onChange={(e) => setCfg({ ...cfg, consent_enabled: e.target.checked })} data-testid="cfg-consent" />
            <div>
              <p className="font-medium text-sm">Termo de Imagem/Voz</p>
              <p className="text-xs text-muted-foreground">Coleta assinatura de autorização</p>
            </div>
          </label>
        </div>
        {slug && (
          <div className="mt-4 pt-4 border-t border-border flex items-center gap-2">
            <Input readOnly value={portalUrl} className="text-xs" data-testid="portal-url" />
            <Button size="sm" variant="outline" onClick={() => { navigator.clipboard.writeText(portalUrl); toast.success("Link copiado"); }}>Copiar</Button>
            <Button size="sm" variant="outline" asChild><a href={portalUrl} target="_blank" rel="noreferrer">Abrir</a></Button>
            <Button size="sm" className="bg-[#D4AF37] hover:bg-[#B8961F] text-white" asChild data-testid="portal-qr-projection-btn">
              <a href={`${window.location.origin}/portal-qr/${slug}`} target="_blank" rel="noreferrer">Projetar QR</a>
            </Button>
          </div>
        )}
        <div className="mt-4">
          <Label>Texto do Termo de Imagem/Voz (variáveis: {`{{empresa_nome}}`})</Label>
          <Textarea rows={6} value={cfg.consent_text} placeholder="Deixe vazio para usar o texto padrão" onChange={(e) => setCfg({ ...cfg, consent_text: e.target.value })} data-testid="consent-text-input" />
        </div>
      </Card>

      <Card className="p-6 shadow-none border-border">
        <p className="overline mb-4">Biblioteca do Aluno ({materials.length})</p>
        <div className="grid grid-cols-12 gap-2 mb-3">
          <Input className="col-span-4" placeholder="Nome do material" value={material.name} onChange={(e) => setMaterial({ ...material, name: e.target.value })} data-testid="material-name" />
          <select className="col-span-2 h-10 px-3 border border-input rounded-md text-sm bg-white" value={material.kind} onChange={(e) => setMaterial({ ...material, kind: e.target.value, url_or_data: "" })}>
            <option value="link">Link externo</option>
            <option value="file">Arquivo (base64)</option>
          </select>
          {material.kind === "link" ? (
            <Input className="col-span-4" placeholder="https://..." value={material.url_or_data} onChange={(e) => setMaterial({ ...material, url_or_data: e.target.value })} data-testid="material-url" />
          ) : (
            <Input className="col-span-4" type="file" accept="application/pdf,image/*,video/*" onChange={onFilePick} data-testid="material-file" />
          )}
          <Button className="col-span-2 bg-[#C07C88] hover:bg-[#A86571]" onClick={addMaterial} data-testid="add-material-btn">Adicionar</Button>
        </div>
        <div className="divide-y divide-border">
          {materials.map((m) => (
            <div key={m.material_id} className="py-2 flex items-center gap-3" data-testid={`material-row-${m.material_id}`}>
              <div className="flex-1 min-w-0">
                <p className="font-medium text-sm truncate">{m.name}</p>
                <p className="text-xs text-muted-foreground truncate">{m.kind === "file" ? "Arquivo" : m.url_or_data}</p>
              </div>
              <button onClick={() => removeMaterial(m.material_id)} className="p-1.5 text-muted-foreground hover:text-red-500"><Trash2 size={13} /></button>
            </div>
          ))}
          {materials.length === 0 && <p className="text-xs text-muted-foreground text-center py-4">Nenhum material</p>}
        </div>
      </Card>

      {consents && (consents.participants.length > 0 || consents.anonymous.length > 0) && (
        <Card className="p-6 shadow-none border-border">
          <p className="overline mb-4">Autorizações de Imagem/Voz ({consents.participants.length + consents.anonymous.length})</p>
          <div className="divide-y divide-border">
            {consents.participants.map((p) => (
              <div key={p.participant_id} className="py-2 flex items-center gap-3">
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-sm">{p.name}</p>
                  <p className="text-xs text-muted-foreground">{p.email} · IP {p.consent?.ip} · {p.consent?.at?.slice(0,16).replace("T"," ")}</p>
                </div>
                <img src={p.consent?.signature_data_url} alt="" className="h-10 border border-border rounded" />
              </div>
            ))}
            {consents.anonymous.map((c) => (
              <div key={`${c.at || ""}-${c.ip || ""}-${(c.signer_email || c.signer_name || "anon")}`} className="py-2 flex items-center gap-3">
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-sm">{c.signer_name || "Anônimo"}</p>
                  <p className="text-xs text-muted-foreground">{c.signer_email} · IP {c.ip} · {c.at?.slice(0,16).replace("T"," ")}</p>
                </div>
                <img src={c.signature_data_url} alt="" className="h-10 border border-border rounded" />
              </div>
            ))}
          </div>
        </Card>
      )}
    </div>
  );
}

function CollabTermCard({ trainingId, training }) {
  const [templates, setTemplates] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({
    template_id: "",
    contratante_razao_social: "",
    contratante_apelido: "",
    contratante_endereco: "",
    contratante_cnpj: "",
    contratante_representante: "",
    local_treinamento: training?.location || "",
    data_inicio: (training?.starts_at || "").slice(0, 10),
    data_fim: "",
    cidade_assinatura: "Canaã dos Carajás-PA",
    data_assinatura: new Date().toISOString().slice(0, 10),
  });

  useEffect(() => {
    api.get("/templates").then((r) => {
      const collab = r.data.filter((t) => t.kind === "collab_term");
      setTemplates(collab);
      if (collab[0]) setForm((f) => ({ ...f, template_id: collab[0].template_id }));
    });
  }, []);

  useEffect(() => {
    setForm((f) => ({ ...f, contratante_razao_social: training?.client_name || f.contratante_razao_social, local_treinamento: training?.location || f.local_treinamento, data_inicio: (training?.starts_at || "").slice(0, 10) || f.data_inicio }));
  }, [training]);

  const generate = async () => {
    if (!form.contratante_razao_social) return toast.error("Preencha a razão social do contratante");
    try {
      const r = await api.post(`/trainings/${trainingId}/collab-term/pdf`, form, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([r.data], { type: "application/pdf" }));
      window.open(url, "_blank");
      setOpen(false);
      toast.success("Termo gerado");
    } catch (e) {
      toast.error("Falha ao gerar PDF");
    }
  };

  return (
    <Card className="p-6 shadow-none border-border">
      <p className="overline mb-3">📝 Termo de Colaboração Técnica</p>
      <p className="text-sm text-muted-foreground mb-4">
        Documento personalizado LEAdera × Contratante para formalizar parceria técnica. Preencha os dados da parte contratante e gere o PDF.
      </p>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogTrigger asChild>
          <Button className="w-full bg-[#D4AF37] hover:bg-[#B8961F] text-white" data-testid="collab-term-btn">
            <FileDown size={14} className="mr-2" /> Gerar Termo de Colaboração
          </Button>
        </DialogTrigger>
        <DialogContent className="max-w-2xl">
          <DialogHeader><DialogTitle>Dados do CONTRATANTE (parte parceira)</DialogTitle></DialogHeader>
          <div className="grid md:grid-cols-2 gap-3">
            <div className="md:col-span-2">
              <Label>Modelo</Label>
              <select value={form.template_id} onChange={(e) => setForm({ ...form, template_id: e.target.value })} className="w-full h-10 px-3 border border-input rounded-md text-sm bg-white" data-testid="collab-template">
                {templates.map((t) => (<option key={t.template_id} value={t.template_id}>{t.name}</option>))}
              </select>
            </div>
            <div className="md:col-span-2"><Label>Razão Social</Label><Input value={form.contratante_razao_social} onChange={(e) => setForm({ ...form, contratante_razao_social: e.target.value })} placeholder="Ex: EMPRESA XYZ LTDA" data-testid="collab-razao" /></div>
            <div><Label>Apelido / Sigla</Label><Input value={form.contratante_apelido} onChange={(e) => setForm({ ...form, contratante_apelido: e.target.value })} placeholder="XYZ" data-testid="collab-apelido" /></div>
            <div><Label>CNPJ</Label><Input value={form.contratante_cnpj} onChange={(e) => setForm({ ...form, contratante_cnpj: e.target.value })} data-testid="collab-cnpj" /></div>
            <div className="md:col-span-2"><Label>Endereço</Label><Input value={form.contratante_endereco} onChange={(e) => setForm({ ...form, contratante_endereco: e.target.value })} data-testid="collab-endereco" /></div>
            <div className="md:col-span-2"><Label>Representante Legal</Label><Input value={form.contratante_representante} onChange={(e) => setForm({ ...form, contratante_representante: e.target.value })} data-testid="collab-rep" /></div>
            <div><Label>Data início</Label><Input type="date" value={form.data_inicio} onChange={(e) => setForm({ ...form, data_inicio: e.target.value })} /></div>
            <div><Label>Data fim</Label><Input type="date" value={form.data_fim} onChange={(e) => setForm({ ...form, data_fim: e.target.value })} /></div>
            <div className="md:col-span-2"><Label>Local do treinamento</Label><Input value={form.local_treinamento} onChange={(e) => setForm({ ...form, local_treinamento: e.target.value })} /></div>
            <div><Label>Cidade assinatura</Label><Input value={form.cidade_assinatura} onChange={(e) => setForm({ ...form, cidade_assinatura: e.target.value })} /></div>
            <div><Label>Data assinatura</Label><Input type="date" value={form.data_assinatura} onChange={(e) => setForm({ ...form, data_assinatura: e.target.value })} /></div>
          </div>
          <DialogFooter>
            <Button className="bg-[#D4AF37] hover:bg-[#B8961F] text-white" onClick={generate} data-testid="collab-generate-btn">
              <FileDown size={14} className="mr-1" /> Gerar PDF
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}
